// Unified Composer (Sprint UI-9 — rebuilt on shadcn primitives).
//
// Two modes:
//   mode='new'      — full-page surface for creating a new agent.
//   mode='continue' — bottom-bar surface for sending follow-ups.
//
// Sprint ai-elements-rendering (Phase 4) — both modes render inside the AI
// Elements composer chrome: the `InputGroup` shell (the exact chrome the
// template's `PromptInput` renders) + context-free PromptInput pieces
// (Header/Footer/Tools/Button). The template's `PromptInput` FORM machinery is
// deliberately NOT mounted: its native drop listeners feed its own blob
// attachment context (ghost chips next to our upload pipeline), its Enter
// handling is unconditional-submit (violates coarse-pointer Enter-inserts-
// newline), and its submit path (FormData + reset + blob→dataURL) conflicts
// with our backend-upload + chip-claiming send. All behaviors below are ours.
//
// Shared across both modes:
//   • InputGroupTextarea (auto-growing) + Send button
//   • AutocompleteDropdown (Sprint 6.7)
//   • Visual viewport tracking (iOS keyboard offset)
//   • Enter to send (desktop), Shift+Enter for newline; on coarse-pointer
//     devices (mobile/tablet) Enter inserts a newline and the Send button is
//     the only way to submit
//
// Mode-specific:
//   'new'      — composerDraft from store, createAgent on send, char counter,
//                memory-constrained warning, Mode+Model+Effort chips.
//   'continue' — localStorage draft persistence, agent-status-aware placeholder,
//                Stop button, Mode+Model+Effort chips + ContextDonut,
//                unrecoverable banner, permission-change confirmation dialog.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Agent, EffortLevel, PermissionMode } from '../../types';
import { useVisualViewport } from '../../hooks/useVisualViewport';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { shouldSendOnEnter } from '../../lib/composer-keys';
import { useAppStore } from '../../stores/app.store';
import { useAutocomplete } from '../../hooks/useAutocomplete';
import { useTranslation } from '../../i18n';
import { AutocompleteDropdown } from '../AutocompleteDropdown';
import { ModelEffortChips } from '../ModelEffortChips';
import { ModeChip } from '../ModeChip';
import { ConfirmDialog } from '../ConfirmDialog';
import { ContextDonut } from '../ContextDonut';
import { ProjectChip } from '../ProjectChip';
import { Icon } from '../atoms/Icon';
import { ApiHttpError, createAgent, patchAgentConfig } from '../../lib/api';
import { useActiveWorkspaceStore } from '../../stores/active-workspace.store';
import { PROMPT_MAX_CHARS, PROMPT_WARN_CHARS, reconcileEffort } from '../../lib/models';
import {
  computeInputBlocked,
  placeholderForStatus,
} from '../../lib/agent-input';
import {
  isPermissionPending,
  isAskUserQuestionPending,
} from '../../lib/permissions/types';
import { Button } from '../ui/button';
import { InputGroup, InputGroupTextarea } from '../ui/input-group';
import {
  PromptInputButton,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputTools,
} from '../ai-elements/prompt-input';
import { cn } from '../../lib/utils';
import { AttachmentChip } from '../AttachmentChip';
import {
  admitFile,
  checkImageDimensions,
  emptyPolicyState,
  filesFromClipboard,
  filesFromDataTransfer,
  type AttachmentChipModel,
  type ChipPolicyState,
  type ChipRoute,
} from '../../lib/attachments';
import { uploadAttachment } from '../../lib/api';
import {
  useChipsForAgent,
  useComposerAttachmentsStore,
} from '../../stores/composer-attachments.store';
import {
  chipToPersisted,
  clearAgentDraft,
  hydrateChip,
  loadAgentDraft,
  saveAgentDraft,
} from '../../lib/composer-attachments-idb';

// ── Layout constants ──────────────────────────────────────────────────

const CONTINUE_MAX_LINES = 6;
const NEW_MIN_LINES = 4;
const NEW_MAX_LINES = 14;
const LINE_HEIGHT_PX = 24;
const PADDING_PX = 16;

// ── Draft persistence (continue mode) ─────────────────────────────────

const DRAFT_KEY_PREFIX = 'claude-dashboard:draft:';
const DRAFT_MAX_BYTES = 100_000;

function readDraft(agentId: string): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY_PREFIX + agentId);
    if (raw === null) return '';
    if (raw.length > DRAFT_MAX_BYTES) return raw.slice(0, DRAFT_MAX_BYTES);
    return raw;
  } catch {
    return '';
  }
}

function writeDraft(agentId: string, text: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (text.length === 0) {
      window.localStorage.removeItem(DRAFT_KEY_PREFIX + agentId);
      return;
    }
    const capped =
      text.length > DRAFT_MAX_BYTES ? text.slice(0, DRAFT_MAX_BYTES) : text;
    window.localStorage.setItem(DRAFT_KEY_PREFIX + agentId, capped);
  } catch {
    // localStorage quota or disabled — ignore.
  }
}

// ── Props ─────────────────────────────────────────────────────────────

export type ComposerProps = ComposerNewProps | ComposerContinueProps;

export interface ComposerNewProps {
  mode: 'new';
  onOpenSidebar?: () => void;
}

export interface ComposerContinueProps {
  mode: 'continue';
  agent: Agent;
  // Sprint 8 — `chipIds` carries the ids of attachments uploaded via
  // POST /api/agents/:id/attachments. Empty/undefined → text-only send.
  onSend: (text: string, chipIds?: string[]) => Promise<void> | void;
  onStop?: () => Promise<void> | void;
  disableViewportTracking?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────

export function Composer(props: ComposerProps) {
  if (props.mode === 'new') {
    return <NewComposer {...props} />;
  }
  return <ContinueComposer {...props} />;
}

// ═══════════════════════════════════════════════════════════════════════
// mode='new' — full-page new-agent surface
// ═══════════════════════════════════════════════════════════════════════

function NewComposer({ onOpenSidebar }: ComposerNewProps) {
  const { t } = useTranslation();
  const draft = useAppStore((s) => s.composerDraft);
  const setComposerPrompt = useAppStore((s) => s.setComposerPrompt);
  const setComposerModel = useAppStore((s) => s.setComposerModel);
  const setComposerEffort = useAppStore((s) => s.setComposerEffort);
  const setComposerPermissionMode = useAppStore((s) => s.setComposerPermissionMode);
  const closeComposer = useAppStore((s) => s.closeComposer);
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const selectAgent = useAppStore((s) => s.selectAgent);
  const memoryConstrained = useAppStore((s) => s.concurrency.memoryConstrained);

  const promptRef = useRef<HTMLTextAreaElement>(null);
  const promptId = useId();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyboardOffset } = useVisualViewport();
  // Touch-primary devices (mobile/tablet): Enter inserts a newline so a
  // founder writing a multi-line prompt by thumb does not accidentally send
  // mid-paragraph. Send is then exclusively driven by the button.
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');

  const autocomplete = useAutocomplete({
    textareaRef: promptRef,
    value: draft.prompt,
    setValue: setComposerPrompt,
    disabled: submitting,
  });

  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const min = NEW_MIN_LINES * LINE_HEIGHT_PX + PADDING_PX;
    const max = NEW_MAX_LINES * LINE_HEIGHT_PX + PADDING_PX;
    el.style.height = `${Math.min(Math.max(el.scrollHeight, min), max)}px`;
  }, [draft.prompt]);

  const charCount = draft.prompt.length;
  const overWarn = charCount > PROMPT_WARN_CHARS;
  const overMax = charCount > PROMPT_MAX_CHARS;
  const trimmed = draft.prompt.trim();
  const canSubmit =
    !submitting && trimmed.length > 0 && !overMax && !memoryConstrained;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const agent = await createAgent({
        prompt: trimmed,
        model: draft.model,
        effort: draft.effort,
        permissionMode: draft.permissionMode,
        projectId: useActiveWorkspaceStore.getState().activeWorkspaceId ?? undefined,
      });
      upsertAgent(agent);
      selectAgent(agent.id);
      closeComposer();
    } catch (err) {
      if (err instanceof ApiHttpError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError(t('composer.couldNotStart'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (autocomplete.handleKeyDown(e)) return;
    if (shouldSendOnEnter(e, { isCoarsePointer })) {
      e.preventDefault();
      void handleSubmit();
    }
  }

  const counterClass = overMax
    ? 'text-destructive font-medium'
    : overWarn
      ? 'text-yellow-500'
      : 'text-muted-foreground/60';

  return (
    <main
      className="flex flex-col h-full w-full bg-background overflow-hidden"
      data-testid="chat-composer"
    >
      <header
        className="flex items-center gap-2 px-2 md:px-4 h-14 border-b border-border bg-background shrink-0"
        data-testid="composer-header"
      >
        {onOpenSidebar && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenSidebar}
            className="md:hidden h-9 w-9 text-muted-foreground"
            aria-label={t('composer.openSidebar')}
            data-testid="composer-open-sidebar"
            style={{ minHeight: 'auto', minWidth: 'auto' }}
          >
            <Icon name="Menu" size={20} aria-hidden="true" />
          </Button>
        )}
        <h1 className="text-xs md:text-sm font-semibold text-foreground">
          {t('composer.heading')}
        </h1>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto flex items-start md:items-center justify-center px-3 md:px-6 py-6">
        <div className="w-full max-w-reading mx-auto space-y-5">
          {memoryConstrained && (
            <div
              role="alert"
              className="rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-500"
              data-testid="composer-memory-warning"
            >
              {t('composer.memoryWarning')}
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="composer-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor={promptId} className="sr-only">
              {t('composer.promptLabel')}
            </label>
            <InputGroup className="bg-secondary dark:bg-secondary">
              <div className="relative w-full">
                <InputGroupTextarea
                  ref={promptRef}
                  id={promptId}
                  value={draft.prompt}
                  onChange={(e) => setComposerPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  enterKeyHint="enter"
                  placeholder={t('composer.placeholder.exampleHint')}
                  autoFocus
                  rows={NEW_MIN_LINES}
                  disabled={submitting}
                  className="px-4 text-sm text-foreground placeholder:text-muted-foreground/50 disabled:opacity-60 leading-6"
                  style={{
                    minHeight: `${NEW_MIN_LINES * LINE_HEIGHT_PX + PADDING_PX}px`,
                    maxHeight: `${NEW_MAX_LINES * LINE_HEIGHT_PX + PADDING_PX}px`,
                  }}
                  data-testid="composer-textarea"
                />
                {autocomplete.trigger && (
                  <AutocompleteDropdown
                    trigger={autocomplete.trigger.char}
                    query={autocomplete.trigger.query}
                    results={autocomplete.results}
                    focusedIndex={autocomplete.focusedIndex}
                    loading={autocomplete.loading}
                    error={autocomplete.error}
                    onSelect={autocomplete.accept}
                    onHoverIndex={autocomplete.setFocusedIndex}
                    onClose={autocomplete.close}
                  />
                )}
              </div>
              {/* onClick={undefined} disables InputGroupAddon's default
                  click-to-focus (it targets `input`, not our textarea). */}
              <PromptInputFooter onClick={undefined} className="flex-wrap gap-3">
                <PromptInputTools className="gap-2 flex-wrap">
                  <ModelEffortChips
                    model={draft.model}
                    effort={draft.effort}
                    onModelChange={setComposerModel}
                    onEffortChange={setComposerEffort}
                    disabled={submitting}
                    size="md"
                  />
                  <ModeChip
                    mode={draft.permissionMode}
                    onChange={setComposerPermissionMode}
                    disabled={submitting}
                    size="md"
                  />
                </PromptInputTools>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs ${counterClass}`}
                    data-testid="composer-char-count"
                  >
                    {charCount.toLocaleString()} /{' '}
                    {PROMPT_MAX_CHARS.toLocaleString()}
                  </span>
                  <Button
                    onClick={() => void handleSubmit()}
                    disabled={!canSubmit}
                    className="px-4 py-2 rounded-full bg-action hover:bg-action/90 text-action-foreground text-sm font-medium"
                    data-testid="composer-submit"
                    style={{ minHeight: 'auto' }}
                  >
                    {submitting ? t('composer.starting') : t('composer.send')}
                  </Button>
                </div>
              </PromptInputFooter>
            </InputGroup>
            {overMax && (
              <p
                className="text-xs text-destructive"
                data-testid="composer-too-long"
              >
                {t('composer.promptTooLong', { count: (charCount - PROMPT_MAX_CHARS).toLocaleString() })}
              </p>
            )}
            {overWarn && !overMax && (
              <p
                className="text-xs text-yellow-500"
                data-testid="composer-warn"
              >
                {t('composer.promptApproachingLimit')}
              </p>
            )}
            <p className="text-xs text-muted-foreground/60">
              {t('composer.enterToSend')}
            </p>
          </div>
        </div>
      </div>

      <div
        aria-hidden="true"
        style={{ height: keyboardOffset || 0 }}
        className="shrink-0"
      />
    </main>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// mode='continue' — bottom-bar follow-up surface
// ═══════════════════════════════════════════════════════════════════════

function ContinueComposer({
  agent,
  onSend,
  onStop,
  disableViewportTracking,
}: ComposerContinueProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => readDraft(agent.id));
  const [sending, setSending] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  // Mid-chat mode switch triggers respawn (the `claude` process is killed
  // and recreated with the new --permission-mode flag). The founder sees
  // a confirm dialog naming the consequence before the respawn happens.
  const [pendingModeChange, setPendingModeChange] = useState<PermissionMode | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upsertAgent = useAppStore((s) => s.upsertAgent);

  // Sprint messages-section-fixes (D-msg-1) — Edit on a user message
  // injects the message text into this composer via the app store. We
  // subscribe here, consume the value once, push it into local `text`
  // state, and focus + scroll the textarea so the founder lands ready
  // to edit.
  const pendingInjection = useAppStore(
    (s) => s.pendingComposerInjection[agent.id] ?? null,
  );
  const consumeComposerInjection = useAppStore((s) => s.consumeComposerInjection);
  useEffect(() => {
    if (pendingInjection === null) return;
    const value = consumeComposerInjection(agent.id);
    if (value === null) return;
    setText(value);
    // Defer focus + caret-to-end so the textarea has the new value
    // committed by the time we adjust selection.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(value.length, value.length);
      el.scrollIntoView({ block: 'end', behavior: 'smooth' });
    });
  }, [pendingInjection, agent.id, consumeComposerInjection]);

  // Sprint composer-draft-attachments-persist — chip state moved out of
  // component-local useState into a per-agent Zustand store so chips
  // survive agent switches. Uploaded chips also survive page reloads
  // via the IndexedDB layer below. Pending/failed chips are intentionally
  // not persisted (resuming an aborted upload on reload is more complex
  // than the founder benefit — see sprint spec §3).
  const chips = useChipsForAgent(agent.id);
  const storeSetChips = useComposerAttachmentsStore((s) => s.setChips);
  const storeUpdateChips = useComposerAttachmentsStore((s) => s.updateChips);
  const storeSetMessageId = useComposerAttachmentsStore((s) => s.setMessageId);
  const storeClearAgent = useComposerAttachmentsStore((s) => s.clearAgent);

  // Adapter that mirrors React's `useState` setter API so existing
  // upload/admit/remove call-sites keep their `setChips(prev => ...)`
  // shape unchanged. Functional updates route through the store's
  // updateChips (read-then-write transactional); array writes route
  // through setChips (replace).
  const setChips = useMemo(
    () =>
      (
        arg:
          | AttachmentChipModel[]
          | ((prev: AttachmentChipModel[]) => AttachmentChipModel[]),
      ) => {
        if (typeof arg === 'function') {
          storeUpdateChips(agent.id, arg);
        } else {
          storeSetChips(agent.id, arg);
        }
      },
    [agent.id, storeSetChips, storeUpdateChips],
  );

  // Per-agent draft messageId — lives in the store too so it survives
  // agent switches. Lazily initialised on first admit (deferred so a
  // hydrate from IDB can restore the persisted messageId without racing
  // a synchronous random-uuid on mount).
  const draftMessageId = useComposerAttachmentsStore(
    (s) => s.byAgent[agent.id]?.messageId ?? null,
  );
  function ensureDraftMessageId(): string {
    if (draftMessageId) return draftMessageId;
    const fresh = crypto.randomUUID();
    storeSetMessageId(agent.id, fresh);
    return fresh;
  }

  // Derived from `chips`. Avoids the "two removeChip calls in one tick read
  // a stale closure of chips" bug that surfaced in Gate 2 review — a
  // useState-on-policy variant required us to recompute on every chip
  // change, which is exactly what useMemo already does.
  const policyState = useMemo<ChipPolicyState>(() => {
    return chips.reduce<ChipPolicyState>((acc, c) => {
      const result = admitFile(c.file, acc, c.messageId, { newId: () => c.id });
      if ('rejected' in result) return acc;
      return result.nextState;
    }, emptyPolicyState());
  }, [chips]);
  const [draggingOver, setDraggingOver] = useState(false);

  // Sprint composer-draft-attachments-persist — IDB hydrate. On agent
  // switch (or first mount), if the store has no chips for this agent
  // yet, try to load a persisted draft from IndexedDB. Persisted chips
  // are reconstructed with their Blob → File so the existing
  // ATTACHMENT_NOT_FOUND recovery (silent re-upload) keeps working if
  // the backend's 24h orphan TTL elapsed. `hydrating` blocks Send while
  // the read is in flight to avoid the race where a user reloads, types
  // text fast, and submits before the chip has come back.
  const [hydrating, setHydrating] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setHydrating(true);
    const liveChips =
      useComposerAttachmentsStore.getState().byAgent[agent.id]?.chips ?? [];
    if (liveChips.length > 0) {
      // The store already holds chips for this agent (in-memory survived
      // an agent switch). Skip IDB — store wins.
      setHydrating(false);
      return;
    }
    void (async () => {
      const persisted = await loadAgentDraft(agent.id);
      if (cancelled) return;
      if (persisted && persisted.chips.length > 0) {
        const hydrated = persisted.chips.map(hydrateChip);
        storeSetChips(agent.id, hydrated);
        storeSetMessageId(agent.id, persisted.messageId);
      }
      setHydrating(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [agent.id, storeSetChips, storeSetMessageId]);

  // Sprint composer-draft-attachments-persist — IDB persist. Whenever
  // the agent's chips change, snapshot uploaded chips to IDB so they
  // survive page reload. Pending/failed are filtered out by
  // chipToPersisted. Debounced via the React effect's natural batching
  // — multiple setChips calls in one tick produce one effect run.
  useEffect(() => {
    if (hydrating) return;
    const persistedChips = chips
      .map(chipToPersisted)
      .filter((c): c is NonNullable<ReturnType<typeof chipToPersisted>> => c !== null);
    if (persistedChips.length === 0) {
      void clearAgentDraft(agent.id);
      return;
    }
    const messageId = draftMessageId ?? persistedChips[0]!.messageId;
    void saveAgentDraft(agent.id, {
      agentId: agent.id,
      messageId,
      chips: persistedChips,
    });
  }, [agent.id, chips, draftMessageId, hydrating]);

  // Cancel uploads on unload (ATT16). Unchanged from Sprint 8 — page
  // unload still aborts in-flight HTTP so the backend's multer/busboy
  // tears down its temp file. Agent switches no longer abort uploads
  // (the previous ATT17 effect is removed; uploads continue in the
  // background and the user finds completed chips on return).
  useEffect(() => {
    const onBeforeUnload = () => {
      for (const c of chips) c.abortController?.abort();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [chips]);

  const messages = useAppStore((s) => s.messagesByAgent[agent.id]);
  const connectionStatus = useAppStore((s) => s.connectionStatus);
  const { keyboardOffset } = useVisualViewport();
  const offset = disableViewportTracking ? 0 : keyboardOffset;
  // Touch-primary devices (mobile/tablet): Enter inserts a newline so a
  // founder writing a multi-line prompt by thumb does not accidentally send
  // mid-paragraph. Send is then exclusively driven by the button.
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');

  const inputBlockedReason = computeInputBlocked(agent);
  const inputBlocked = inputBlockedReason !== 'none';

  const autocomplete = useAutocomplete({
    textareaRef,
    value: text,
    setValue: setText,
    disabled: inputBlocked || sending,
  });

  const canStop = !!onStop && agent.status === 'running';

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = CONTINUE_MAX_LINES * LINE_HEIGHT_PX + PADDING_PX;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [text]);

  const textOwnerRef = useRef(agent.id);
  useEffect(() => {
    textOwnerRef.current = agent.id;
    setText(readDraft(agent.id));
  }, [agent.id]);

  // Cursor mirror — opening/switching a conversation leaves the caret in the
  // composer. Desktop only: on touch devices this would pop the keyboard on
  // every chat tap.
  useEffect(() => {
    if (isCoarsePointer) return;
    textareaRef.current?.focus();
  }, [agent.id, isCoarsePointer]);

  useEffect(() => {
    if (textOwnerRef.current !== agent.id) return;
    writeDraft(agent.id, text);
  }, [agent.id, text]);

  // Sprint 8 — chips count toward send-readiness. Send is allowed if there
  // is text OR at least one uploaded chip; pending chips block send (ATT14)
  // until they finish or the user removes them.
  //
  // Sprint composer-draft-attachments-persist — we do NOT gate canSend
  // on `hydrating`. The race window is one microtask (IDB read resolves
  // before the next paint), and a user who reloads-and-immediately-sends
  // would not have seen the chip rail render anyway. If they submit
  // text without the persisted chip, they get a normal send and can
  // re-attach + re-send. Pre-mortem failure mode #2 mitigation was
  // overzealous for the actual risk profile, and gating broke baseline
  // send tests that don't wait for the hydrate microtask.
  const hasUploadedChip = chips.some((c) => c.state === 'uploaded');
  const hasPendingChip = chips.some((c) => c.state === 'pending');
  const hasFailedChip = chips.some((c) => c.state === 'failed');
  const canSend =
    !sending &&
    !inputBlocked &&
    !hasPendingChip &&
    !hasFailedChip &&
    (text.trim().length > 0 || hasUploadedChip);

  // Upload a single chip, updating its state as it progresses. Emits to the
  // backend's POST /api/agents/:id/attachments. On error, surfaces a 'failed'
  // state with a retry affordance.
  async function uploadChip(chip: AttachmentChipModel): Promise<void> {
    const ctrl = new AbortController();
    setChips((prev) =>
      prev.map((c) =>
        c.id === chip.id ? { ...c, state: 'pending', progress: 0.1, abortController: ctrl, errorMessage: undefined } : c,
      ),
    );
    try {
      const resp = await uploadAttachment({
        agentId: agent.id,
        chipId: chip.id,
        messageId: chip.messageId,
        file: chip.file,
        signal: ctrl.signal,
      });
      setChips((prev) =>
        prev.map((c) =>
          c.id === chip.id
            ? {
                ...c,
                state: 'uploaded',
                progress: 1,
                serverRoute: resp.route as ChipRoute,
              }
            : c,
        ),
      );
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Subida falló';
      setChips((prev) =>
        prev.map((c) =>
          c.id === chip.id ? { ...c, state: 'failed', errorMessage: message } : c,
        ),
      );
    }
  }

  // Admit a list of files — applies the policy gate, kicks off uploads for
  // every accepted chip. Surfaces rejected files via the inline error line
  // so the founder sees a single message even on a multi-file drop.
  // Note: `policyState` is derived (useMemo) so we don't write it back; the
  // local `nextPolicy` accumulator only sequences the admit-checks for the
  // batch we're about to add.
  async function admitFiles(files: File[]) {
    if (files.length === 0) return;
    let nextPolicy = policyState;
    const accepted: AttachmentChipModel[] = [];
    const rejections: string[] = [];
    for (const f of files) {
      // Image dimension pre-flight: Anthropic vision rejects >8000×8000 px.
      // We decode the file just enough to read naturalWidth/Height before
      // policy gating so the founder sees a clean rejection inline rather
      // than an opaque API error after the upload completes.
      const mime = f.type || '';
      if (mime.startsWith('image/')) {
        const dim = await checkImageDimensions(f);
        if ('rejected' in dim) {
          rejections.push(`${f.name}: ${dim.rejected}`);
          continue;
        }
      }
      const result = admitFile(f, nextPolicy, ensureDraftMessageId(), {
        newId: () => crypto.randomUUID(),
      });
      if ('rejected' in result) {
        rejections.push(`${f.name}: ${result.rejected.reason}`);
        continue;
      }
      accepted.push(result.chip);
      nextPolicy = result.nextState;
    }
    if (accepted.length > 0) {
      setChips((prev) => [...prev, ...accepted]);
      for (const c of accepted) void uploadChip(c);
    }
    if (rejections.length > 0) {
      setError(rejections.join(' • '));
    } else {
      setError(null);
    }
  }

  function removeChip(id: string) {
    setChips((prev) => {
      const c = prev.find((x) => x.id === id);
      c?.abortController?.abort();
      return prev.filter((x) => x.id !== id);
    });
    // policyState is derived from chips via useMemo — it recomputes
    // automatically on the next render. No manual rebuild needed.
  }

  function retryChip(id: string) {
    const target = chips.find((c) => c.id === id);
    if (!target) return;
    void uploadChip(target);
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = filesFromClipboard(e.clipboardData?.items ?? null);
    if (files.length === 0) return; // plain-text paste falls through (ATT2)
    e.preventDefault();
    void admitFiles(files);
  }

  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDraggingOver(false);
    const dt = e.dataTransfer;
    const { files, rejectedFolderCount } = await filesFromDataTransfer(dt.items);
    if (rejectedFolderCount > 0) {
      setError('Suelta archivos individuales, no carpetas.');
    }
    void admitFiles(files);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      setDraggingOver(true);
    }
  }

  function handleDragLeave() {
    setDraggingOver(false);
  }

  function handlePickerChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    void admitFiles(files);
    // Reset so the same file can be picked again right after.
    e.target.value = '';
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  // Translate the raw backend "Adjunto no encontrado o caducado: <UUID>"
  // wording into a founder-mode line. Singular if the draft has one chip,
  // plural otherwise. Strips the UUID, names the next action.
  function attachmentLostMessage(chipCount: number): string {
    return chipCount <= 1 ? t('composer.attachmentLost') : t('composer.attachmentsLost');
  }

  // Re-upload every chip that still has its File reference and retry the
  // send once. Returns ok=true if the retry succeeded; otherwise a reason
  // the caller maps to a founder-mode message. Silent — does not mutate
  // chip.state, so the UI does not flash 'pending'.
  async function recoverAttachmentsAndRetrySend(
    payload: string,
    chipsAtSendTime: AttachmentChipModel[],
  ): Promise<{ ok: true } | { ok: false }> {
    const recoverable = chipsAtSendTime.filter(
      (c) => c.state === 'uploaded' && c.file instanceof File,
    );
    if (recoverable.length === 0) return { ok: false };
    try {
      await Promise.all(
        recoverable.map((c) =>
          uploadAttachment({
            agentId: agent.id,
            chipId: c.id,
            messageId: c.messageId,
            file: c.file,
          }),
        ),
      );
    } catch {
      return { ok: false };
    }
    const chipIds = recoverable.map((c) => c.id);
    try {
      await onSend(payload, chipIds);
      return { ok: true };
    } catch {
      // Second failure (any reason). Caller surfaces founder-mode wording.
      return { ok: false };
    }
  }

  async function handleSend() {
    if (!canSend) return;
    const payload = text.trim();
    const uploadedChips = chips.filter((c) => c.state === 'uploaded');
    const chipIds = uploadedChips.map((c) => c.id);
    setSending(true);
    setError(null);
    try {
      // Conditional arity preserves the legacy onSend(text) signature for
      // call-sites and tests that haven't been migrated to the chips API.
      if (chipIds.length > 0) {
        await onSend(payload, chipIds);
      } else {
        await onSend(payload);
      }
      setText('');
      writeDraft(agent.id, '');
      storeClearAgent(agent.id);
      void clearAgentDraft(agent.id);
    } catch (err) {
      // ATTACHMENT_NOT_FOUND fires when the backend's in-memory store has
      // swept the chip (TTL > now-createdAt) or was restarted. The File
      // reference still lives in the browser, so we can re-upload silently
      // and retry the send once. If recovery fails (no File ref, second
      // ATTACHMENT_NOT_FOUND, or upload error), surface a founder-mode
      // line that names the next action — never the raw UUID.
      if (
        err instanceof ApiHttpError &&
        err.code === 'ATTACHMENT_NOT_FOUND' &&
        uploadedChips.length > 0
      ) {
        const recovery = await recoverAttachmentsAndRetrySend(payload, uploadedChips);
        if (recovery.ok) {
          setText('');
          writeDraft(agent.id, '');
          storeClearAgent(agent.id);
          void clearAgentDraft(agent.id);
        } else {
          setError(attachmentLostMessage(uploadedChips.length));
        }
      } else {
        setError(err instanceof Error ? err.message : t('composer.sendFailed'));
      }
    } finally {
      setSending(false);
    }
  }

  async function handleStop() {
    if (!canStop || stopping) return;
    setStopping(true);
    try {
      await onStop?.();
    } finally {
      setStopping(false);
    }
  }

  async function applyConfig(next: {
    model?: string;
    effort?: EffortLevel;
    permissionMode?: PermissionMode;
  }) {
    const optimistic: Agent = {
      ...agent,
      model: next.model ?? agent.model,
      effort: next.effort ?? agent.effort,
      permissionMode: next.permissionMode ?? agent.permissionMode ?? 'agent',
    };
    upsertAgent(optimistic);
    setConfigError(null);
    try {
      const updated = await patchAgentConfig(agent.id, next);
      upsertAgent(updated);
    } catch (err) {
      upsertAgent(agent);
      setConfigError(
        err instanceof Error ? err.message : t('composer.couldNotChangeAgent'),
      );
    }
  }

  function handleModelChange(nextModel: string) {
    const nextEffort = reconcileEffort(
      nextModel,
      agent.effort as EffortLevel,
    );
    void applyConfig({
      model: nextModel,
      effort: nextEffort !== agent.effort ? nextEffort : undefined,
    });
  }

  function handleEffortChange(nextEffort: EffortLevel) {
    void applyConfig({ effort: nextEffort });
  }

  function handleModeChange(nextMode: PermissionMode) {
    const currentMode = agent.permissionMode ?? 'agent';
    if (nextMode === currentMode) return;
    setPendingModeChange(nextMode);
  }

  function confirmModeChange() {
    const next = pendingModeChange;
    setPendingModeChange(null);
    if (next === null) return;
    void applyConfig({ permissionMode: next });
  }

  function hasPendingPermission(): boolean {
    const list = messages ?? [];
    for (let i = list.length - 1; i >= 0; i--) {
      const m = list[i];
      if (!m) continue;
      if (
        m.type === 'permission_request' &&
        isPermissionPending(m.content)
      )
        return true;
      if (
        m.type === 'ask_user_question' &&
        isAskUserQuestionPending(m.content)
      )
        return true;
    }
    return false;
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (autocomplete.handleKeyDown(e)) return;
    if (!shouldSendOnEnter(e, { isCoarsePointer })) return;
    e.preventDefault();
    void handleSend();
  }

  if (inputBlockedReason === 'unrecoverable') {
    return <UnrecoverableBanner agent={agent} offset={offset} />;
  }
  if (inputBlockedReason === 'archived') {
    return <ArchivedBanner offset={offset} />;
  }

  const showStop = canStop;
  const sendEnabled = canSend;
  const buttonAction: 'send' | 'stop' | 'disabled' = showStop
    ? 'stop'
    : sendEnabled
      ? 'send'
      : 'disabled';

  return (
    <>
      <div
        className="border-t border-border bg-secondary px-3 md:px-4 pt-3 pb-3 md:pb-4 transition-[padding,transform] duration-150"
        style={{
          paddingBottom: `calc(0.75rem + env(safe-area-inset-bottom) + ${offset}px)`,
        }}
        data-testid="chat-input-container"
      >
       <div className="max-w-reading mx-auto">

        {configError && (
          <p
            className="mb-2 text-xs text-destructive"
            role="alert"
            data-testid="composer-config-error"
          >
            {configError}
          </p>
        )}

        <InputGroup
          className={cn(
            'bg-secondary dark:bg-secondary transition-colors duration-150',
            draggingOver && 'border-primary',
          )}
          data-testid="composer-card"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => void handleDrop(e)}
        >
          {chips.length > 0 && (
            /* onClick={undefined} disables InputGroupAddon's default
               click-to-focus (it targets `input`, which here would focus the
               hidden file picker and interfere with chip interactions). */
            <PromptInputHeader
              onClick={undefined}
              className="gap-2 px-3 pt-2 pb-1"
              data-testid="composer-chip-row"
            >
              {chips.map((c) => (
                <AttachmentChip
                  key={c.id}
                  id={c.id}
                  route={c.predictedRoute}
                  name={c.displayName}
                  size={c.size}
                  state={c.state}
                  progress={c.progress}
                  errorMessage={c.errorMessage}
                  onRemove={removeChip}
                  onRetry={c.state === 'failed' ? retryChip : undefined}
                  agentId={agent.id}
                  localFile={c.file}
                />
              ))}
            </PromptInputHeader>
          )}
          <div className="relative w-full">
            {/* Hidden picker lives inside the wrapper (NOT as a direct
                InputGroup child): the shell's `[&>input]` / `has-[>input]`
                selectors key off direct input children. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.md,.txt,.csv,.json,.xml,.html,.log,.ipynb,.yaml,.yml,text/*"
              className="sr-only"
              onChange={handlePickerChange}
              data-testid="composer-file-picker"
            />
            <InputGroupTextarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              enterKeyHint="enter"
              rows={1}
              disabled={inputBlocked || sending}
              placeholder={placeholderForStatus({
                status: agent.status,
                hasSession: !!agent.claudeSessionId,
                isDisconnected: connectionStatus === 'disconnected',
                isArchived: agent.archived,
                hasPermissionPending: hasPendingPermission(),
              })}
              className="px-3 py-2 text-foreground placeholder:text-muted-foreground/40 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                minHeight: `${LINE_HEIGHT_PX + PADDING_PX}px`,
                maxHeight: `${CONTINUE_MAX_LINES * LINE_HEIGHT_PX + PADDING_PX}px`,
                lineHeight: `${LINE_HEIGHT_PX}px`,
              }}
              data-testid="chat-textarea"
            />
            {autocomplete.trigger && (
              <AutocompleteDropdown
                trigger={autocomplete.trigger.char}
                query={autocomplete.trigger.query}
                results={autocomplete.results}
                focusedIndex={autocomplete.focusedIndex}
                loading={autocomplete.loading}
                error={autocomplete.error}
                onSelect={autocomplete.accept}
                onHoverIndex={autocomplete.setFocusedIndex}
                onClose={autocomplete.close}
              />
            )}
          </div>

          <PromptInputFooter
            onClick={undefined}
            className="px-2 pb-2 pt-1"
            data-testid="composer-toolbar"
          >
            <PromptInputTools className="min-w-0 overflow-x-auto">
              <ModelEffortChips
                model={agent.model}
                effort={agent.effort as EffortLevel}
                onModelChange={handleModelChange}
                onEffortChange={handleEffortChange}
              />
              <ModeChip
                mode={agent.permissionMode ?? 'agent'}
                onChange={handleModeChange}
              />
              <ContextDonut pct={agent.contextUsedPct ?? null} />
              <ProjectChip agent={agent} />
            </PromptInputTools>
            <PromptInputTools className="shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={openFilePicker}
                aria-label={t('composer.attachAriaLabel')}
                data-testid="composer-attach-button"
                className="lg:hidden shrink-0 h-11 w-11 p-0 text-muted-foreground hover:text-foreground"
                style={{ minHeight: 'auto', minWidth: 'auto' }}
              >
                <Icon name="Paperclip" size={18} aria-hidden="true" />
              </Button>
              <FusedSendStopButton
                action={buttonAction}
                sending={sending}
                stopping={stopping}
                onSend={() => void handleSend()}
                onStop={() => void handleStop()}
                sendLabel={t('composer.sendAriaLabel')}
                stopLabel={t('composer.stopAriaLabel')}
              />
            </PromptInputTools>
          </PromptInputFooter>
        </InputGroup>
        {error && (
          <p
            className="mt-2 text-xs text-destructive"
            data-testid="chat-input-error"
            role="alert"
          >
            {error}
          </p>
        )}

       </div>

      </div>
      <ConfirmDialog
        open={pendingModeChange !== null}
        title={t('confirm.changeMode.title', {
          mode: pendingModeChange ? t(`modeChip.${pendingModeChange}` as const) : '',
        })}
        body={t('confirm.changeMode.body')}
        confirmLabel={t('confirm.changeMode.confirm')}
        onConfirm={confirmModeChange}
        onCancel={() => setPendingModeChange(null)}
      />
    </>
  );
}

// ── Fused Send/Stop button ─────────────────────────────────────────────
//
// Tri-state (send / stop / disabled) is ours — the template's
// PromptInputSubmit status button is replaced by this extension so Stop
// stays a first-class action (D-008: composer is the resume; no separate
// Retry/Resume buttons). Chrome: PromptInputButton icon-sm sizing.
function FusedSendStopButton(props: {
  action: 'send' | 'stop' | 'disabled';
  sending: boolean;
  stopping: boolean;
  onSend: () => void;
  onStop: () => void;
  sendLabel: string;
  stopLabel: string;
}) {
  const { action, sending, stopping, onSend, onStop, sendLabel, stopLabel } = props;
  if (action === 'stop') {
    return (
      <PromptInputButton
        size="icon-sm"
        onClick={onStop}
        disabled={stopping}
        aria-label={stopLabel}
        data-testid="composer-stop-button"
        className="shrink-0 text-action hover:text-action/80"
        style={{ minHeight: 'auto', minWidth: 'auto' }}
      >
        <Icon name="Square" size={14} aria-hidden="true" />
      </PromptInputButton>
    );
  }
  const disabled = action === 'disabled' || sending;
  return (
    <PromptInputButton
      size="icon-sm"
      onClick={onSend}
      disabled={disabled}
      aria-label={sendLabel}
      data-testid="chat-send-button"
      className={`shrink-0 ${disabled ? 'text-muted-foreground' : 'text-action hover:text-action/80'}`}
      style={{ minHeight: 'auto', minWidth: 'auto' }}
    >
      <Icon name="ArrowUp" size={18} aria-hidden="true" />
    </PromptInputButton>
  );
}

// ── Unrecoverable banner ─────────────────────────────────────────────

function UnrecoverableBanner({
  agent,
  offset,
}: {
  agent: Agent;
  offset: number;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="border-t border-border bg-background px-3 md:px-4 pt-4 pb-4"
      style={{
        paddingBottom: `calc(1rem + env(safe-area-inset-bottom) + ${offset}px)`,
      }}
      data-testid="unrecoverable-banner"
      role="status"
    >
      <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-destructive">
          {t('composer.unrecoverable.message')}
        </p>
        <Button
          variant="default"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(
              new CustomEvent('as-oasis:fork-agent', {
                detail: { agentId: agent.id },
              }),
            );
          }}
          className="shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground"
          data-testid="unrecoverable-fork-cta"
          style={{ minHeight: 'auto' }}
        >
          {t('composer.unrecoverable.forkCta')}
        </Button>
      </div>
    </div>
  );
}

// Oficina v2 (S1, T1.2) — un agente archivado no recibe mensajes: se dice, sin caja de texto.
function ArchivedBanner({ offset }: { offset: number }) {
  const { t } = useTranslation();
  return (
    <div
      className="border-t border-border bg-background px-3 md:px-4 pt-4 pb-4"
      style={{ paddingBottom: `calc(1rem + env(safe-area-inset-bottom) + ${offset}px)` }}
      data-testid="archived-banner"
      role="status"
    >
      <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
        {t('composer.archived.message')}
      </p>
    </div>
  );
}
